import type { TFunction } from "i18next";

import type { Facet } from "../types";

/**
 * O texto de tela de uma faceta (AB#85634, task #86093).
 *
 * === Por que o identificador não se traduz ===
 *
 * `name` e `value` são IDENTIFICADORES: o banco exige minúsculas
 * (`CHECK (name = lower(name))`), a unicidade é por eles e todo filtro casa por
 * igualdade exata. Traduzi-los criaria duas facetas para a mesma coisa —
 * `department:marketing` e `departamento:marketing` — e a busca quebraria em um
 * dos idiomas.
 *
 * Então o que se traduz é o RÓTULO, guardado ao lado, na coluna `labels`:
 *
 *   labels: { name:  { pt: "Departamento", en: "Department" },
 *             value: { pt: "Marketing",    en: "Marketing" } }
 *
 * === A cascata ===
 *
 * 1. o rótulo cadastrado no idioma pedido;
 * 2. o rótulo em qualquer outro idioma, quando o pedido falta — um texto escrito
 *    por alguém vale mais que um slug, mesmo na língua errada;
 * 3. a tradução embutida da aplicação (`facets.labels.*`), que cobre o
 *    vocabulário padrão semeado pelo produto;
 * 4. o próprio identificador.
 *
 * A tela nunca fica em branco, e nenhuma faceta existente muda de aparência
 * enquanto ninguém escrever rótulo nenhum.
 */
const doCadastro = (
  mapa: Record<string, string> | undefined,
  idioma: string,
): string | undefined => {
  if (!mapa) return undefined;

  // `pt-BR` casa com `pt`: a aplicação guarda o rótulo pela língua, e não pela
  // região.
  const curto = idioma.split("-")[0] ?? idioma;
  const escolhido = mapa[idioma]?.trim() || mapa[curto]?.trim();
  if (escolhido) return escolhido;

  return Object.values(mapa)
    .find((texto) => texto?.trim())
    ?.trim();
};

/** A tradução embutida, quando existe — `t` devolve a própria chave se não. */
const daAplicacao = (chave: string, t: TFunction): string | undefined => {
  const traduzido = t(chave);

  return traduzido === chave ? undefined : traduzido;
};

/** O rótulo da DIMENSÃO (`name`): "Departamento" para `department`. */
export const rotuloDaDimensao = (
  name: string,
  idioma: string,
  t: TFunction,
  facet?: Pick<Facet, "labels"> | null,
): string =>
  doCadastro(facet?.labels?.name, idioma) ?? daAplicacao(`facets.labels.${name}`, t) ?? name;

/** O rótulo do VALOR: "Marketing" para `marketing`. */
export const rotuloDoValor = (
  value: string,
  idioma: string,
  t: TFunction,
  facet?: Pick<Facet, "labels"> | null,
): string =>
  doCadastro(facet?.labels?.value, idioma) ??
  daAplicacao(`facets.labels.values.${value}`, t) ??
  value;
